import {
	Button,
	Currency,
	Form,
	Grid,
	Header,
	Icon,
	Modal,
} from '@mairistem/composants';
import { Sens } from '@mairistem/comptabilite';
import { formatMontant, getErrorMessage } from '@mairistem/finances-utils';
import { isNewIdentifiant, newIdentifiant, request } from '@mairistem/tools';
import Decimal from 'decimal.js';
import { isNil } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import STORAGE_KEY from '../../../constants/common/storage';
import type Financement from '../../../interfaces/common/financement';
import type Projet from '../../../interfaces/common/projet';
import './ReechelonementCreditModal.css';

interface ReechelonementCreditModalProps {
	onClose: () => void;
	onSuccess?: () => void;
	disponible: number;
	identifiantProjet: number;
	identifiantComposant?: number;
	exercice?: string;
}

const ReechelonementCreditModal = ({
	onClose,
	onSuccess,
	disponible = 0,
	identifiantProjet,
	identifiantComposant,
	exercice,
}: ReechelonementCreditModalProps) => {
	const [data, setData] = React.useState<Projet>();
	const [loadingUpdate, setLoadingUpdate] = React.useState(false);

	// Depuis une ligne composant, seuls les financements de ce composant sont concernés
	const isComposantCible = (identifiant: number | string) =>
		isNil(identifiantComposant) || identifiant === identifiantComposant;
	const composantsCibles = (data?.composants || []).filter((c) =>
		isComposantCible(c.identifiant),
	);
	const loadData = async () => {
		try {
			const params = new URLSearchParams({
				withComposants: 'true',
				withFinancements: 'true',
				sens: Sens.DEPENSE,
			});
			const response = await request.get<unknown, Projet>(
				`/api/finances/simulation/apcp/projets/${identifiantProjet}?${params.toString()}`,
				{ params },
			);
			setData(response);
		} catch {
			toast.error('Erreur lors du chargement des données');
		}
	};

	const handleBlurMontant = (
		montant: number,
		identifiantFinancement: number | string,
		identifiantComposantFinancement?: number,
	) => {
		if (isNil(identifiantComposantFinancement)) {
			setData((old) => ({
				...old,
				financements: old.financements.map((f) =>
					f.identifiant === identifiantFinancement
						? { ...f, reechelonement: montant }
						: f,
				),
			}));
		} else {
			setData((old) => ({
				...old,
				composants: old.composants.map((c) =>
					c.identifiant === identifiantComposantFinancement
						? {
								...c,
								financements: c.financements.map((f) =>
									f.identifiant === identifiantFinancement
										? { ...f, reechelonement: montant }
										: f,
								),
							}
						: c,
				),
			}));
		}
	};

	const handleDeleteFinancement = (identifiantFinancement: number | string) => {
		if (isNil(identifiantComposant)) {
			setData((old) => ({
				...old,
				financements: old.financements.filter(
					(f) => f.identifiant !== identifiantFinancement,
				),
			}));
		} else {
			setData((old) => ({
				...old,
				composants: old.composants.map((c) =>
					c.identifiant === identifiantComposant
						? {
								...c,
								financements: c.financements.filter(
									(f) => f.identifiant !== identifiantFinancement,
								),
							}
						: c,
				),
			}));
		}
	};

	const handleConfirm = async () => {
		let totalReechelonement = new Decimal(0);
		data.financements.forEach((f) => {
			totalReechelonement = totalReechelonement.add(f.reechelonement || 0);
		});
		composantsCibles.forEach((c) => {
			c.financements.forEach((f) => {
				totalReechelonement = totalReechelonement.add(f.reechelonement || 0);
			});
		});

		if (!totalReechelonement.eq(disponible)) {
			toast.error(
				`Le total des rééchelonnements (${formatMontant(totalReechelonement.toNumber())}) doit être égal au montant disponible (${formatMontant(disponible)})`,
			);
			return;
		}

		const updatedData = {
			...data,
			financements: data?.financements.map((f) => {
				if (f.exercice.toString() === exercice) {
					return {
						...f,
						revision: new Decimal(f.revision || 0)
							.sub(totalReechelonement)
							.toNumber(),
					};
				}

				return {
					...f,
					revision: new Decimal(f?.revision || 0)
						.add(f.reechelonement || 0)
						.toNumber(),
				};
			}),
			composants:
				data?.composants.map((c) => {
					if (!isComposantCible(c.identifiant)) {
						return c;
					}
					return {
						...c,
						financements: c.financements.map((f) => {
							if (f.exercice.toString() === exercice) {
								return {
									...f,
									revision: new Decimal(f.revision || 0)
										.sub(totalReechelonement)
										.toNumber(),
								};
							}
							return {
								...f,
								revision: new Decimal(f?.revision || 0)
									.add(f.reechelonement || 0)
									.toNumber(),
							};
						}),
					};
				}) || [],
		};

		try {
			setLoadingUpdate(true);
			const identifiantEntiteCompta = sessionStorage.getItem(
				STORAGE_KEY.ID_ENTITE_CPTA,
			);
			await request.put(
				`/api/finances/simulation/apcp/projets/${identifiantProjet}?skipCheckImputations=true`,
				{ ...updatedData, identifiantEntiteCompta },
			);
			onSuccess?.();
			onClose();
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					"Erreur lors de l'enregistrement du rééchelonnement",
				),
			);
		} finally {
			setLoadingUpdate(false);
		}
	};

	const renderFinancements = (
		financements: Financement[],
		identifiantComposantFinancement?: number,
	) =>
		financements
			.filter((f) => Number(f.exercice) > Number(exercice))
			.map((financement) => (
				<Grid.Row
					key={financement.identifiant}
					verticalAlign="middle"
					textAlign="right"
				>
					<Grid.Column width={2}>{financement.exercice}</Grid.Column>
					<Grid.Column width={4}>
						{formatMontant(financement.revision)}
					</Grid.Column>
					<Grid.Column width={6} className="montant-reechelonement-column">
						<Currency
							data-testid={`montantReechelonment-${financement.exercice}`}
							onBlur={(_e, montant: number) =>
								handleBlurMontant(
									montant,
									financement.identifiant,
									identifiantComposantFinancement,
								)
							}
							fluid
							value={financement.reechelonement || 0}
						/>
						{isNewIdentifiant(financement.identifiant) && (
							<Icon
								iconSet="Lucide"
								name="Trash2"
								size={16}
								color="red"
								cursor="pointer"
								onClick={() => handleDeleteFinancement(financement.identifiant)}
							/>
						)}
					</Grid.Column>
					<Grid.Column width={4}>
						{formatMontant(
							new Decimal(financement?.revision || 0)
								.add(financement?.reechelonement || 0)
								.toNumber(),
						)}
					</Grid.Column>
				</Grid.Row>
			));

	const handleCreateFinancement = () => {
		const allExercices = [
			...(data?.financements || []).map((f) => f.exercice),
			...composantsCibles.flatMap((c) =>
				(c.financements || []).map((f) => f.exercice),
			),
		];
		const maxExercice =
			allExercices.length > 0
				? Math.max(...allExercices)
				: new Date().getFullYear();

		const financement = {
			identifiant: newIdentifiant(),
			identifiantEntite: data.identifiantEntite,
			identifiantComposant: null,
			identifiantProjet,
			exercice: maxExercice + 1,
			revision: 0,
			sens: Sens.DEPENSE,
			type: null,
			reechelonement: 0,
		};

		if (isNil(identifiantComposant)) {
			setData((old) => ({
				...old,
				financements: [...old.financements, financement],
			}));
		} else {
			financement.identifiantComposant = identifiantComposant;
			setData((old) => ({
				...old,
				composants: old.composants.map((c) =>
					c.identifiant === identifiantComposant
						? {
								...c,
								financements: [...(c.financements || []), financement],
							}
						: c,
				),
			}));
		}
	};

	return (
		<Modal
			closeIcon
			closeOnDimmerClick={false}
			onClose={onClose}
			open
			size="small"
			onMount={() => loadData()}
		>
			<Modal.Header
				content={`Rééchelonnement des crédits (${formatMontant(disponible)})`}
			/>
			<Modal.Content>
				<Grid verticalAlign="middle">
					<Grid.Column floated="left" width={10}>
						<Header size="medium">
							Répartition sur les exercices ultérieurs:
						</Header>
					</Grid.Column>
					<Grid.Column floated="right" width={6} textAlign="right">
						<Button
							content="Ajouter année"
							primary
							icon="plus"
							onClick={handleCreateFinancement}
						/>
					</Grid.Column>
				</Grid>

				<Form>
					<Grid>
						<Grid.Row>
							<Grid.Column width={2} />
							<Grid.Column width={4} textAlign="right">
								<strong>Déjà existant</strong>
							</Grid.Column>
							<Grid.Column width={6} textAlign="right">
								<strong>Saisie cours</strong>
							</Grid.Column>
							<Grid.Column width={4} textAlign="right">
								<strong>Nouveau total</strong>
							</Grid.Column>
						</Grid.Row>

						{renderFinancements(data?.financements || [])}
						{composantsCibles.map((composant) =>
							renderFinancements(
								composant.financements || [],
								composant.identifiant as number,
							),
						)}
						{(() => {
							const totalSaisi = [
								...(data?.financements || []),
								...composantsCibles.flatMap((c) => c.financements || []),
							].reduce(
								(acc, f) => acc.add(f.reechelonement || 0),
								new Decimal(0),
							);
							return (
								<Grid.Row>
									<Grid.Column width={2} />
									<Grid.Column width={4} />
									<Grid.Column
										width={6}
										className="montant-reechelonement-column"
									>
										<div style={{ width: '100%' }}>
											<div className="total-column">
												<span>Total réparti :</span>
												<strong>{formatMontant(totalSaisi.toNumber())}</strong>
											</div>
											<div className="total-column">
												<span>Reste à répartir :</span>
												<strong>
													{formatMontant(
														new Decimal(disponible).sub(totalSaisi).toNumber(),
													)}
												</strong>
											</div>
										</div>
									</Grid.Column>
									<Grid.Column width={4} />
								</Grid.Row>
							);
						})()}
					</Grid>
				</Form>
			</Modal.Content>
			<Modal.Actions>
				<Button
					content="Annuler"
					data-testid="reechelonementCreditModalCancelButton"
					onClick={() => onClose()}
				/>
				<Button
					content="Appliquer"
					data-testid="reechelonementCreditModalCreateButton"
					disabled={loadingUpdate}
					loading={loadingUpdate}
					onClick={handleConfirm}
					positive
				/>
			</Modal.Actions>
		</Modal>
	);
};

export default ReechelonementCreditModal;
